import { Button, Tag } from '@carbon/react';
import { CheckmarkFilled, Close } from '@carbon/react/icons';
import { useSetAtom } from 'jotai';

import { tasksPanelOpenAtom } from './atoms';
import { useWorkspaceConfirmation } from './workspace-confirmation-context';
import styles from './tasks-panel.module.scss';

/**
 * Inner content for the Tasks side sidebar. The workspace has exactly ONE
 * task — "Certify asset interest" — complete when the workspace is promoted
 * (mirrors the Type Curve workspace's verification step). Mounted by
 * `page.tsx` inside a `DualSidebarLayout` right slot so opening the panel
 * pushes the comparison table left instead of overlaying it.
 *
 * @return {JSX.Element} The rendered panel content.
 */
export const TasksPanel = (): JSX.Element => {
  const setOpen = useSetAtom(tasksPanelOpenAtom);
  const { workspace } = useWorkspaceConfirmation();
  const completed = workspace.promoted;

  return (
    <div className={styles.root}>
      <div className={styles.panelHeader}>
        <div className={styles.headerCopy}>
          <h2 className={styles.title}>Tasks</h2>
          <p className={styles.subtitle}>{completed ? 1 : 0} of 1 complete</p>
        </div>
        <Button
          hasIconOnly
          size="lg"
          kind="ghost"
          onClick={() => setOpen(false)}
          iconDescription="Close tasks panel"
          tooltipAlignment="center"
          tooltipPosition="bottom"
          renderIcon={(props) => <Close size={20} {...props} />}
        />
      </div>
      <div className={styles.body}>
        <div
          className={
            completed ? styles.statusBarComplete : styles.statusBarPending
          }
          aria-hidden
        />
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Interest Certification</span>
          <span className={styles.sectionMeta}>{completed ? 1 : 0}/1</span>
        </div>
        <hr className={styles.rule} />
        <div className={styles.taskRow}>
          {completed ? (
            <div className={styles.taskIconComplete} aria-hidden>
              <CheckmarkFilled size={16} className={styles.taskIconCheck} />
            </div>
          ) : (
            <span className={styles.taskIconPending} aria-hidden />
          )}
          <div>
            <div className={styles.taskTitleRow}>
              <span className={styles.taskName}>Certify asset interest</span>
              <Tag type={completed ? 'green' : 'gray'} size="sm">
                {completed ? 'Certified' : 'Pending'}
              </Tag>
            </div>
            <p className={styles.taskHint}>
              Certify the slot interests per subtract, then Promote this
              workspace to complete this task.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
